import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { KuiModule } from 'app/key-ui';
import { EventsService, PollService } from 'app/services';
import { EventFilterService } from 'app/services/events/event-filter.service';
import { KeyMapModule, MapSetService } from 'app/shared/components';
import { SharedModule } from 'app/shared/shared.module';
import { AdminBulkConfirmModalComponent } from './confirm/confirm-modal.component';
import { AdminContactEditorComponent } from './editor/contacts/contact-editor.component';
import { EntityEditorComponent } from './editor/editor.component';
import { AdminFormEditorComponent } from './editor/form/form-editor.component';
import { AdminListEditorComponent } from './editor/list/list-editor.component';
import { AdminSectionEditorComponent } from './editor/section/section.component';
import { AdminZoneEditorComponent } from './editor/zone/zone-editor.component';
import { AlertHandler } from './entities/alert/handler';
import { AssetHandler } from './entities/asset/handler';
import { AssetGroupHandler } from './entities/assetgroup/handler';
import { AssetRatingProfileHandler } from './entities/assetratingprofile/handler';
import { AssetStateProfileHandler } from './entities/assetstateprofile/handler';
import { OverspeedProfileHandler } from './entities/overspeedprofile/handler';
import { AssetTagHandler } from './entities/assettag/handler';
import { AssetCategoryHandler } from './entities/category/handler';
import { ClientHandler } from './entities/client/handler';
import { CompanyGroupHandler } from './entities/companygroup/handler';
import { CostCentreHandler } from './entities/costcentre/handler';
import { CustomFieldHandler } from './entities/customfields/handler';
import { DashboardHandler } from './entities/dashboard/handler';
import { DeviceHandler } from './entities/device/handler';
import { DistributorHandler } from './entities/distributor/handler';
import { IoTypeHandler } from './entities/iotype/handler';
import { SimCardHandler } from './entities/simcard/handler';
import { ThemeHandler } from './entities/theme/handler';
import { UserHandler } from './entities/user/handler';
import { UserRoleHandler } from './entities/userrole/handler';
import { VendorHandler } from './entities/vendor/handler';
import { ZoneHandler } from './entities/zone/handler';
import { ZoneGroupHandler } from './entities/zonegroup/handler';
import { AdminProgressModalComponent } from './progress/progress-modal.component';
import { AdminSelectModalComponent } from './select/select.component';
import { AdminWizardModalComponent } from './wizard/wizard-modal.component';
import { RouterModule } from '@angular/router';
import { DashboardTemplateHandler } from './entities/dashboardtemplates/handler';
import { ScheduledReportHandler } from './entities/scheduledreport/handler';
import { AdminInlineListEditorComponent } from './editor/inline-list/inline-list-editor.component';
import { KeySharedReportsModule } from '../reports/reports.module';
import { AdminEditorService } from './admin.service';
import { EditDashboardModalComponent } from './dashboard/dashboard-modal.component';
import { KeyDashboardsModule } from '../dashboards/dashboards.module';
import { OidcAuthService } from 'app/services/oidc/oidc.service';
import { ThemeJsonModalComponent } from './entities/theme/json-modal/json-modal.component';
import { DeviceProviderHandler } from './entities/deviceproviders/handler';
import { ExportTaskHandler } from './entities/exporttasks/handler';
import { HealthIssuesHandler } from './entities/healthissues/handler';
import { LocationSelectModalModule } from '../modal/location-select-modal/location-select.module';
import { SmsGatewayProviderHandler } from './entities/smsgatewayprovider/handler';
import { EmailProviderHandler } from './entities/emailprovider/handler';
import { SslCertificateHandler } from './entities/sslcertificate/handler';
import { MapSetHandler } from './entities/mapset/handler';
import { AdminConsoleModalComponent } from './console/console.component';
import { HealthIssueService } from './entities/healthissues/healthissue.service';
import { AdminChartComponent } from './editor/chart/chart.component';
import { SystemHandler } from './entities/system/handler';
import { DeviceTypeHandler } from './entities/devicetype/handler';
import { TaskHandler } from './entities/tasks/handler';
import { MediaHandler } from './entities/media/handler';
import { AdminMediaComponent } from './editor/media/media.component';
import { AdminCustomEditorComponent } from './editor/custom/custom.component';
import { RoadProfileHandler } from './entities/roadprofile/handler';
import { GeoLockProfileHandler } from './entities/geolockprofile/handler';
import { ThemeCssModalComponent } from './entities/theme/css-modal/css-modal.component';
import { AdminEditorModalComponent } from './modal/editor-modal.component';
import { AnalyticsBaseReportsHandler } from './entities/analyticsbasereport/handler';
import { FuelCardHandler } from './entities/fuelcard/handler';
import { DeviceConfigProfileHandler } from './entities/deviceconfigprofile/handler';
import { AdminFeedComponent } from './editor/feed/feed.component';
import { LabelHandler } from './entities/label/handler';
import { PrivacyProfileHandler } from './entities/privacyprofile/handler';

const COMPONENTS = [
    EntityEditorComponent,
    AdminProgressModalComponent,
    AdminEditorModalComponent,
    AdminBulkConfirmModalComponent,
    AdminFormEditorComponent,
    AdminSelectModalComponent,
    AdminWizardModalComponent,
    AdminSectionEditorComponent,
    AdminContactEditorComponent,
    AdminListEditorComponent,
    AdminInlineListEditorComponent,
    AdminCustomEditorComponent,
    AdminZoneEditorComponent,
    AdminChartComponent,
    AdminMediaComponent,
    AdminFeedComponent,
    EditDashboardModalComponent,
    ThemeJsonModalComponent,
    ThemeCssModalComponent,
    AdminConsoleModalComponent,
];

@NgModule({
    imports: [
        CommonModule,
        KuiModule,
        SharedModule,
        FormsModule,
        RouterModule,
        KeyMapModule,
        HttpClientModule,
        KeySharedReportsModule,
        KeyDashboardsModule,
        LocationSelectModalModule,
    ],
    declarations: COMPONENTS,
    exports: COMPONENTS,
    providers: [
        AssetHandler,
        AssetTagHandler,
        UserHandler,
        UserRoleHandler,
        ClientHandler,
        VendorHandler,
        SimCardHandler,
        FuelCardHandler,
        ExportTaskHandler,
        DistributorHandler,
        SystemHandler,
        DeviceHandler,
        DeviceConfigProfileHandler,
        AssetGroupHandler,
        CompanyGroupHandler,
        CostCentreHandler,
        AssetCategoryHandler,
        ZoneGroupHandler,
        DashboardHandler,
        DashboardTemplateHandler,
        ScheduledReportHandler,
        IoTypeHandler,
        AlertHandler,
        ZoneHandler,
        ThemeHandler,
        AssetStateProfileHandler,
        AssetRatingProfileHandler,
        OverspeedProfileHandler,
        GeoLockProfileHandler,
        RoadProfileHandler,
        CustomFieldHandler,
        PollService,
        MapSetService,
        TranslateService,
        EventsService,
        EventFilterService,
        AdminEditorService,
        OidcAuthService,
        DeviceProviderHandler,
        DeviceTypeHandler,
        HealthIssueService,
        HealthIssuesHandler,
        SmsGatewayProviderHandler,
        EmailProviderHandler,
        SslCertificateHandler,
        MapSetHandler,
        TaskHandler,
        MediaHandler,
        AnalyticsBaseReportsHandler,
        LabelHandler,
        PrivacyProfileHandler,
    ]
})
export class AdminEditorModule { }
